import { useParams } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { EmbedFormNotFoundError, useEmbedForm } from "./use-embed-form";
import { EmbedFormRenderer } from "./embed-form-renderer";

// Phase 7 sub-bucket 19 — real embed surface (replaces the sub-bucket 2
// placeholder).
//
// Public, anonymous, iframable from any third-party origin. The CSP for
// `/embed/*` lives in `apps/web/public/_headers` and drops X-Frame-Options
// + relaxes frame-ancestors to `*` for this route only.
//
// The embed flow:
//   1. GET /api/forms/:slug (anonymous, public) — returns the published form.
//   2. Render via <EmbedFormRenderer> which POSTs to /api/forms/:slug/submit
//      with `credentials: 'omit'` (cross-origin iframes can't carry the
//      parent app's auth cookie).
//
// `<EmbedLayout>` (apps/web/src/features/embed/embed-layout.tsx) already
// strips chrome + forces transparent body background, so the page only
// needs to render the form card.
export function EmbedFormPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const query = useEmbedForm(slug);

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border bg-white p-6 shadow-sm">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="mt-3 h-4 w-1/2" />
        <Skeleton className="mt-6 h-40 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const notFound = query.error instanceof EmbedFormNotFoundError;
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border bg-white p-8 text-center shadow-sm">
        <h1 className="font-display text-xl font-semibold tracking-tight">
          {notFound ? "Form not found" : "Couldn’t load this form"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {notFound
            ? "It may have been unpublished. Please contact whoever shared this link."
            : "Please refresh the page. If the problem persists, contact support."}
        </p>
      </div>
    );
  }

  const form = query.data;
  const definition = form.definition;

  return (
    <div className="rounded-2xl border bg-white p-6 shadow-sm">
      <header className="mb-6 border-b border-border/70 pb-4">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {definition.title}
        </h1>
        {form.description || definition.description ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {form.description ?? definition.description}
          </p>
        ) : null}
      </header>
      <EmbedFormRenderer form={form} />
    </div>
  );
}
